"use client";

import { useEffect, useMemo, useState } from "react";
import styles from "./page.module.css";

type JournalTrade = {
  id: string;
  symbol: string;
  direction: "LONG" | "SHORT";
  quantity: number;
  entry_price: number;
  entry_date: string;
  exit_price?: number;
  exit_date?: string;
  stop_loss?: number;
  take_profit?: number;
  setup?: string;
  timeframe?: string;
  strategy?: string;
  reason?: string;
  plan?: string;
  emotions?: string;
  mistakes?: string;
  lessons?: string;
  tags: string[];
  rating?: number;
  created_at?: string;
  updated_at?: string;
};

type JournalReview = {
  id: string;
  date: string;
  title: string;
  summary?: string;
  wins?: string;
  losses?: string;
  improvements?: string;
};

type JournalMilestone = {
  id: string;
  title: string;
  date: string;
  status: "open" | "done";
  notes?: string;
};

type MissedTrade = {
  id: string;
  symbol: string;
  date: string;
  setup?: string;
  timeframe?: string;
  trigger?: string;
  reason?: string;
  notes?: string;
  tags: string[];
};

type JournalPayload = {
  trades: JournalTrade[];
  missed_trades: MissedTrade[];
  reviews: JournalReview[];
  milestones: JournalMilestone[];
};

const LOCAL_KEY = "psx_trade_journal_v1";
const AUTH_TOKEN_KEY = "psx_auth_token";

const formatNumber = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const todayISO = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const buildId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const emptyTradeDraft = (): JournalTrade => ({
  id: "",
  symbol: "",
  direction: "LONG",
  quantity: 0,
  entry_price: 0,
  entry_date: todayISO(),
  tags: [],
});

const emptyReview = (): JournalReview => ({
  id: "",
  date: todayISO(),
  title: "",
});

const emptyMilestone = (): JournalMilestone => ({
  id: "",
  title: "",
  date: todayISO(),
  status: "open",
});

const emptyMissedTrade = (): MissedTrade => ({
  id: "",
  symbol: "",
  date: todayISO(),
  tags: [],
});

const parseTags = (raw: string) =>
  raw
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

const formatTags = (tags: string[]) => tags.join(", ");

export default function TradeJournalPage() {
  const [token, setToken] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");
  const [journal, setJournal] = useState<JournalPayload>(() => {
    if (typeof window === "undefined") {
      return { trades: [], missed_trades: [], reviews: [], milestones: [] };
    }
    try {
      const stored = localStorage.getItem(LOCAL_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.data) {
          return parsed.data as JournalPayload;
        }
        return parsed as JournalPayload;
      }
    } catch {}
    return { trades: [], missed_trades: [], reviews: [], milestones: [] };
  });

  const [tradeDraft, setTradeDraft] = useState<JournalTrade>(emptyTradeDraft());
  const [tradeTagsText, setTradeTagsText] = useState("");
  const [editingTradeId, setEditingTradeId] = useState<string | null>(null);

  const [missedDraft, setMissedDraft] = useState<MissedTrade>(emptyMissedTrade());
  const [missedTagsText, setMissedTagsText] = useState("");
  const [editingMissedId, setEditingMissedId] = useState<string | null>(null);

  const [reviewDraft, setReviewDraft] = useState<JournalReview>(emptyReview());
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null);

  const [milestoneDraft, setMilestoneDraft] = useState<JournalMilestone>(emptyMilestone());
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setToken(localStorage.getItem(AUTH_TOKEN_KEY));
  }, []);

  useEffect(() => {
    const loadJournal = async () => {
      if (!token) {
        setLoaded(true);
        return;
      }
      try {
        const response = await fetch("/api/trade-journal", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.status === 401 || response.status === 403) {
          if (typeof window !== "undefined") {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem("psx_auth_email");
          }
          setToken(null);
          setLoaded(true);
          return;
        }
        if (!response.ok) throw new Error("Failed to load");
        const data = await response.json();
        if (data?.data) {
          setJournal((prev) => ({
            trades: data.data.trades ?? prev.trades,
            missed_trades: data.data.missed_trades ?? prev.missed_trades ?? [],
            reviews: data.data.reviews ?? prev.reviews,
            milestones: data.data.milestones ?? prev.milestones,
          }));
        }
      } catch {
      } finally {
        setLoaded(true);
      }
    };
    loadJournal();
  }, [token]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(LOCAL_KEY, JSON.stringify({ saved_at: new Date().toISOString(), data: journal }));
  }, [journal]);

  useEffect(() => {
    if (!loaded || !token) return;
    const timer = setTimeout(async () => {
      try {
        const response = await fetch("/api/trade-journal", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ data: journal }),
        });
        if (!response.ok) throw new Error("Failed");
        setSyncStatus("Saved");
        setTimeout(() => setSyncStatus(""), 1500);
      } catch {
        setSyncStatus("Save failed");
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [journal, loaded, token]);

  const handleTradeSubmit = () => {
    const requiredFields = [
      tradeDraft.symbol,
      tradeDraft.entry_date,
      tradeDraft.entry_price,
      tradeDraft.quantity,
      tradeDraft.reason,
    ];
    if (requiredFields.some((v) => v === undefined || v === "" || v === 0)) {
      alert("Please fill symbol, entry date, entry price, quantity, and reason.");
      return;
    }

    const now = new Date().toISOString();
    const tags = parseTags(tradeTagsText);
    const payload: JournalTrade = {
      ...tradeDraft,
      symbol: tradeDraft.symbol.toUpperCase(),
      quantity: Number(tradeDraft.quantity),
      entry_price: Number(tradeDraft.entry_price),
      exit_price: tradeDraft.exit_price ? Number(tradeDraft.exit_price) : undefined,
      stop_loss: tradeDraft.stop_loss ? Number(tradeDraft.stop_loss) : undefined,
      take_profit: tradeDraft.take_profit ? Number(tradeDraft.take_profit) : undefined,
      tags,
      updated_at: now,
      created_at: editingTradeId ? tradeDraft.created_at : now,
      id: editingTradeId ?? buildId(),
    };

    setJournal((prev) => ({
      ...prev,
      trades: editingTradeId
        ? prev.trades.map((t) => (t.id === editingTradeId ? payload : t))
        : [payload, ...prev.trades],
    }));
    setTradeDraft(emptyTradeDraft());
    setTradeTagsText("");
    setEditingTradeId(null);
  };

  const handleTradeEdit = (trade: JournalTrade) => {
    setTradeDraft({ ...trade });
    setTradeTagsText(formatTags(trade.tags));
    setEditingTradeId(trade.id);
  };

  const handleTradeDelete = (id: string) => {
    if (!confirm("Delete this trade?")) return;
    setJournal((prev) => ({ ...prev, trades: prev.trades.filter((t) => t.id !== id) }));
  };

  const handleMissedSubmit = () => {
    if (!missedDraft.symbol || !missedDraft.date) {
      alert("Please fill symbol and date.");
      return;
    }
    const payload: MissedTrade = {
      ...missedDraft,
      symbol: missedDraft.symbol.toUpperCase(),
      tags: parseTags(missedTagsText),
      id: editingMissedId ?? buildId(),
    };
    setJournal((prev) => ({
      ...prev,
      missed_trades: editingMissedId
        ? prev.missed_trades.map((t) => (t.id === editingMissedId ? payload : t))
        : [payload, ...prev.missed_trades],
    }));
    setMissedDraft(emptyMissedTrade());
    setMissedTagsText("");
    setEditingMissedId(null);
  };

  const handleMissedEdit = (trade: MissedTrade) => {
    setMissedDraft({ ...trade });
    setMissedTagsText(formatTags(trade.tags));
    setEditingMissedId(trade.id);
  };

  const handleMissedDelete = (id: string) => {
    if (!confirm("Delete this missed trade?")) return;
    setJournal((prev) => ({ ...prev, missed_trades: prev.missed_trades.filter((t) => t.id !== id) }));
  };

  const handleReviewSubmit = () => {
    if (!reviewDraft.title || !reviewDraft.date) {
      alert("Please fill review title and date.");
      return;
    }
    const payload: JournalReview = {
      ...reviewDraft,
      id: editingReviewId ?? buildId(),
    };
    setJournal((prev) => ({
      ...prev,
      reviews: editingReviewId
        ? prev.reviews.map((r) => (r.id === editingReviewId ? payload : r))
        : [payload, ...prev.reviews],
    }));
    setReviewDraft(emptyReview());
    setEditingReviewId(null);
  };

  const handleReviewEdit = (review: JournalReview) => {
    setReviewDraft({ ...review });
    setEditingReviewId(review.id);
  };

  const handleReviewDelete = (id: string) => {
    if (!confirm("Delete this review?")) return;
    setJournal((prev) => ({ ...prev, reviews: prev.reviews.filter((r) => r.id !== id) }));
  };

  const handleMilestoneSubmit = () => {
    if (!milestoneDraft.title || !milestoneDraft.date) {
      alert("Please fill milestone title and date.");
      return;
    }
    const payload: JournalMilestone = {
      ...milestoneDraft,
      id: editingMilestoneId ?? buildId(),
    };
    setJournal((prev) => ({
      ...prev,
      milestones: editingMilestoneId
        ? prev.milestones.map((m) => (m.id === editingMilestoneId ? payload : m))
        : [payload, ...prev.milestones],
    }));
    setMilestoneDraft(emptyMilestone());
    setEditingMilestoneId(null);
  };

  const handleMilestoneEdit = (milestone: JournalMilestone) => {
    setMilestoneDraft({ ...milestone });
    setEditingMilestoneId(milestone.id);
  };

  const handleMilestoneDelete = (id: string) => {
    if (!confirm("Delete this milestone?")) return;
    setJournal((prev) => ({ ...prev, milestones: prev.milestones.filter((m) => m.id !== id) }));
  };

  const closedTrades = journal.trades.filter((t) => t.exit_price && t.exit_date);
  const openTrades = journal.trades.filter((t) => !t.exit_price);

  const pnlByTrade = (trade: JournalTrade) => {
    if (!trade.exit_price) return 0;
    const diff =
      trade.direction === "SHORT"
        ? trade.entry_price - trade.exit_price
        : trade.exit_price - trade.entry_price;
    return diff * trade.quantity;
  };

  const riskByTrade = (trade: JournalTrade) => {
    if (!trade.stop_loss) return 0;
    const riskPerShare =
      trade.direction === "SHORT"
        ? trade.stop_loss - trade.entry_price
        : trade.entry_price - trade.stop_loss;
    return riskPerShare * trade.quantity;
  };

  const analytics = useMemo(() => {
    const totalClosed = closedTrades.length;
    const totalPnL = closedTrades.reduce((sum, t) => sum + pnlByTrade(t), 0);
    const wins = closedTrades.filter((t) => pnlByTrade(t) > 0);
    const losses = closedTrades.filter((t) => pnlByTrade(t) < 0);
    const winRate = totalClosed ? (wins.length / totalClosed) * 100 : 0;
    const profit = wins.reduce((sum, t) => sum + pnlByTrade(t), 0);
    const loss = losses.reduce((sum, t) => sum + Math.abs(pnlByTrade(t)), 0);
    const profitFactor = loss ? profit / loss : profit > 0 ? 9.99 : 0;
    const rValues = closedTrades
      .map((t) => {
        const risk = riskByTrade(t);
        return risk ? pnlByTrade(t) / risk : 0;
      })
      .filter((v) => v !== 0);
    const avgR = rValues.length ? rValues.reduce((a, b) => a + b, 0) / rValues.length : 0;
    return {
      totalClosed,
      totalPnL,
      winRate,
      profitFactor,
      avgR,
      openCount: openTrades.length,
    };
  }, [closedTrades, openTrades]);

  const monthlyPnL = useMemo(() => {
    const map = new Map<string, number>();
    closedTrades.forEach((trade) => {
      const key = (trade.exit_date || trade.entry_date).slice(0, 7);
      map.set(key, (map.get(key) ?? 0) + pnlByTrade(trade));
    });
    return Array.from(map.entries())
      .map(([month, value]) => ({ month, value }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [closedTrades]);

  const maxMonthlyPnL = Math.max(1, ...monthlyPnL.map((m) => Math.abs(m.value)));

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.hero}>
          <div>
            <h1 className={styles.heroTitle}>Trade Journal</h1>
            <p className={styles.heroSubtitle}>
              Capture your thesis, execution, and review. Track why you entered, how you managed risk,
              and what you learned so every trade compounds your edge.
            </p>
          </div>
          <div className={styles.heroMeta}>
            <span className={styles.pill}>Session ready</span>
            <span className={styles.pill}>Sync: {syncStatus || (token ? "Auto" : "Local")}</span>
            <span className={styles.pill}>Trades: {journal.trades.length}</span>
          </div>
          <div className={styles.sectionNav}>
            <a className={styles.navButton} href="#analytics">Analytics</a>
            <a className={styles.navButton} href="#review">Review</a>
            <a className={styles.navButton} href="#trades">Trades DB</a>
            <a className={styles.navButton} href="#missed">Missed Trades</a>
            <a className={styles.navButton} href="#milestones">Milestones</a>
          </div>
        </header>

        {!token && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>Login Required</div>
              <div className={styles.cardHint}>Your journal can still be drafted locally.</div>
            </div>
            <p className={styles.muted}>
              Log in to sync trades to your account. Drafts are saved locally until you sign in.
            </p>
          </div>
        )}

        <section className={styles.card} id="capture">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>{editingTradeId ? "Edit Trade" : "New Trade Capture"}</div>
            <div className={styles.cardHint}>Record why you took the trade at entry.</div>
          </div>
          <div className={styles.formGrid}>
            <input
              className={styles.input}
              placeholder="Symbol (e.g. OGDC)"
              value={tradeDraft.symbol}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, symbol: e.target.value }))}
            />
            <select
              className={styles.select}
              value={tradeDraft.direction}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, direction: e.target.value as "LONG" | "SHORT" }))}
            >
              <option value="LONG">Long</option>
              <option value="SHORT">Short</option>
            </select>
            <input
              className={styles.input}
              type="number"
              placeholder="Quantity"
              value={tradeDraft.quantity || ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, quantity: Number(e.target.value) }))}
            />
            <input
              className={styles.input}
              type="number"
              placeholder="Entry Price"
              value={tradeDraft.entry_price || ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, entry_price: Number(e.target.value) }))}
            />
            <input
              className={styles.input}
              type="date"
              value={tradeDraft.entry_date}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, entry_date: e.target.value }))}
            />
            <input
              className={styles.input}
              type="number"
              placeholder="Stop Loss"
              value={tradeDraft.stop_loss ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, stop_loss: Number(e.target.value) || undefined }))}
            />
            <input
              className={styles.input}
              type="number"
              placeholder="Take Profit"
              value={tradeDraft.take_profit ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, take_profit: Number(e.target.value) || undefined }))}
            />
            <input
              className={styles.input}
              type="number"
              placeholder="Exit Price"
              value={tradeDraft.exit_price ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, exit_price: Number(e.target.value) || undefined }))}
            />
            <input
              className={styles.input}
              type="date"
              value={tradeDraft.exit_date ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, exit_date: e.target.value || undefined }))}
            />
            <input
              className={styles.input}
              placeholder="Setup"
              value={tradeDraft.setup ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, setup: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Timeframe (e.g. 4H)"
              value={tradeDraft.timeframe ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, timeframe: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Strategy"
              value={tradeDraft.strategy ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, strategy: e.target.value }))}
            />
            <input
              className={styles.input}
              type="number"
              placeholder="Rating (1-5)"
              value={tradeDraft.rating ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, rating: Number(e.target.value) || undefined }))}
            />
            <input
              className={styles.input}
              placeholder="Tags (comma separated)"
              value={tradeTagsText}
              onChange={(e) => setTradeTagsText(e.target.value)}
            />
            <textarea
              className={styles.textarea}
              placeholder="Reason for entry"
              value={tradeDraft.reason ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, reason: e.target.value }))}
            />
            <textarea
              className={styles.textarea}
              placeholder="Plan and management notes"
              value={tradeDraft.plan ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, plan: e.target.value }))}
            />
            <textarea
              className={styles.textarea}
              placeholder="Emotions / mistakes / lessons"
              value={tradeDraft.lessons ?? ""}
              onChange={(e) => setTradeDraft((prev) => ({ ...prev, lessons: e.target.value }))}
            />
          </div>
          <div className={styles.formActions}>
            <button className={styles.primaryButton} type="button" onClick={handleTradeSubmit}>
              {editingTradeId ? "Update Trade" : "Add Trade"}
            </button>
            {editingTradeId && (
              <button
                className={styles.ghostButton}
                type="button"
                onClick={() => {
                  setTradeDraft(emptyTradeDraft());
                  setTradeTagsText("");
                  setEditingTradeId(null);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </section>

        <section className={`${styles.card}`} id="analytics">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Analytics Snapshot</div>
            <div className={styles.cardHint}>Closed trades drive performance metrics.</div>
          </div>
          <div className={styles.kpiGrid}>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>Closed Trades</div>
              <div className={styles.kpiValue}>{analytics.totalClosed}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>Open Positions</div>
              <div className={styles.kpiValue}>{analytics.openCount}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>Win Rate</div>
              <div className={styles.kpiValue}>{analytics.winRate.toFixed(1)}%</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>Profit Factor</div>
              <div className={styles.kpiValue}>{analytics.profitFactor.toFixed(2)}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>Avg R</div>
              <div className={styles.kpiValue}>{analytics.avgR.toFixed(2)}</div>
            </div>
            <div className={styles.kpi}>
              <div className={styles.kpiLabel}>Total P&L (PKR)</div>
              <div className={styles.kpiValue}>{formatNumber(analytics.totalPnL, 0)}</div>
            </div>
          </div>
          <div className={styles.chartRow}>
            {monthlyPnL.length === 0 && <div className={styles.muted}>No closed trades yet.</div>}
            {monthlyPnL.map((month) => {
              const width = Math.min(100, (Math.abs(month.value) / maxMonthlyPnL) * 100);
              const color =
                month.value >= 0 ? "linear-gradient(90deg, var(--color-success), #14b8a6)" : "linear-gradient(90deg, var(--color-accent), var(--color-danger))";
              return (
                <div key={month.month}>
                  <div className={styles.muted}>{month.month}</div>
                  <div className={styles.barTrack}>
                    <div className={styles.barFill} style={{ width: `${width}%`, background: color }} />
                  </div>
                  <div className={styles.muted}>PKR {formatNumber(month.value, 0)}</div>
                </div>
              );
            })}
          </div>
        </section>

        <section className={styles.card} id="review">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Review & Reflection</div>
            <div className={styles.cardHint}>Weekly or monthly review notes.</div>
          </div>
          <div className={styles.formGrid}>
            <input
              className={styles.input}
              type="date"
              value={reviewDraft.date}
              onChange={(e) => setReviewDraft((prev) => ({ ...prev, date: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Review title"
              value={reviewDraft.title}
              onChange={(e) => setReviewDraft((prev) => ({ ...prev, title: e.target.value }))}
            />
            <textarea
              className={styles.textarea}
              placeholder="Summary"
              value={reviewDraft.summary ?? ""}
              onChange={(e) => setReviewDraft((prev) => ({ ...prev, summary: e.target.value }))}
            />
            <textarea
              className={styles.textarea}
              placeholder="What went well"
              value={reviewDraft.wins ?? ""}
              onChange={(e) => setReviewDraft((prev) => ({ ...prev, wins: e.target.value }))}
            />
            <textarea
              className={styles.textarea}
              placeholder="What went wrong"
              value={reviewDraft.losses ?? ""}
              onChange={(e) => setReviewDraft((prev) => ({ ...prev, losses: e.target.value }))}
            />
            <textarea
              className={styles.textarea}
              placeholder="What to improve"
              value={reviewDraft.improvements ?? ""}
              onChange={(e) => setReviewDraft((prev) => ({ ...prev, improvements: e.target.value }))}
            />
          </div>
          <div className={styles.formActions}>
            <button className={styles.primaryButton} type="button" onClick={handleReviewSubmit}>
              {editingReviewId ? "Update Review" : "Add Review"}
            </button>
            {editingReviewId && (
              <button
                className={styles.ghostButton}
                type="button"
                onClick={() => {
                  setReviewDraft(emptyReview());
                  setEditingReviewId(null);
                }}
              >
                Cancel
              </button>
            )}
          </div>
          <div className={styles.listGrid} style={{ marginTop: "1rem" }}>
            {journal.reviews.map((review) => (
              <div key={review.id} className={styles.listCard}>
                <div className={styles.listTitle}>{review.title}</div>
                <div className={styles.muted}>{review.date}</div>
                {review.summary && <div className={styles.muted}>{review.summary}</div>}
                <div className={styles.rowActions} style={{ marginTop: "0.4rem" }}>
                  <button className={styles.ghostButton} type="button" onClick={() => handleReviewEdit(review)}>Edit</button>
                  <button className={styles.ghostButton} type="button" onClick={() => handleReviewDelete(review.id)}>Delete</button>
                </div>
              </div>
            ))}
            {journal.reviews.length === 0 && <div className={styles.muted}>No reviews yet.</div>}
          </div>
        </section>

        <section className={styles.card} id="trades">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Trades Database</div>
            <div className={styles.cardHint}>Detailed log with rationale and outcomes.</div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Direction</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>P&L</th>
                  <th>Reason</th>
                  <th>Tags</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {journal.trades.map((trade) => {
                  const pnl = pnlByTrade(trade);
                  const statusClass = trade.exit_price
                    ? pnl >= 0
                      ? styles.statusClosed
                      : styles.statusLoss
                    : styles.statusOpen;
                  return (
                    <tr key={trade.id}>
                      <td>{trade.symbol}</td>
                      <td>{trade.direction}</td>
                      <td>{trade.entry_price} @ {trade.entry_date}</td>
                      <td>{trade.exit_price ? `${trade.exit_price} @ ${trade.exit_date ?? "-"}` : "Open"}</td>
                      <td>
                        <span className={`${styles.chip} ${statusClass}`}>
                          {trade.exit_price ? `PKR ${formatNumber(pnl, 0)}` : "Open"}
                        </span>
                      </td>
                      <td className={styles.muted}>{trade.reason || "-"}</td>
                      <td>
                        {trade.tags.length > 0
                          ? trade.tags.map((tag) => (
                              <span key={tag} className={styles.tag}>{tag}</span>
                            ))
                          : "-"}
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          <button className={styles.ghostButton} type="button" onClick={() => handleTradeEdit(trade)}>Edit</button>
                          <button className={styles.ghostButton} type="button" onClick={() => handleTradeDelete(trade.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {journal.trades.length === 0 && (
                  <tr>
                    <td colSpan={8} className={styles.muted}>No trades logged yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.card} id="missed">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Missed Trades DB</div>
            <div className={styles.cardHint}>Capture the setups you skipped.</div>
          </div>
          <div className={styles.formGrid}>
            <input
              className={styles.input}
              placeholder="Symbol"
              value={missedDraft.symbol}
              onChange={(e) => setMissedDraft((prev) => ({ ...prev, symbol: e.target.value }))}
            />
            <input
              className={styles.input}
              type="date"
              value={missedDraft.date}
              onChange={(e) => setMissedDraft((prev) => ({ ...prev, date: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Setup"
              value={missedDraft.setup ?? ""}
              onChange={(e) => setMissedDraft((prev) => ({ ...prev, setup: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Timeframe"
              value={missedDraft.timeframe ?? ""}
              onChange={(e) => setMissedDraft((prev) => ({ ...prev, timeframe: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Trigger"
              value={missedDraft.trigger ?? ""}
              onChange={(e) => setMissedDraft((prev) => ({ ...prev, trigger: e.target.value }))}
            />
            <input
              className={styles.input}
              placeholder="Tags (comma separated)"
              value={missedTagsText}
              onChange={(e) => setMissedTagsText(e.target.value)}
            />
            <textarea
              className={styles.textarea}
              placeholder="Reason for skipping / notes"
              value={missedDraft.reason ?? ""}
              onChange={(e) => setMissedDraft((prev) => ({ ...prev, reason: e.target.value }))}
            />
          </div>
          <div className={styles.formActions}>
            <button className={styles.primaryButton} type="button" onClick={handleMissedSubmit}>
              {editingMissedId ? "Update Missed Trade" : "Add Missed Trade"}
            </button>
            {editingMissedId && (
              <button
                className={styles.ghostButton}
                type="button"
                onClick={() => {
                  setMissedDraft(emptyMissedTrade());
                  setMissedTagsText("");
                  setEditingMissedId(null);
                }}
              >
                Cancel
              </button>
            )}
          </div>
          <div className={styles.listGrid} style={{ marginTop: "1rem" }}>
            {journal.missed_trades.map((trade) => (
              <div key={trade.id} className={styles.listCard}>
                <div className={styles.listTitle}>{trade.symbol}</div>
                <div className={styles.muted}>{trade.date} · {trade.setup || "Setup"} · {trade.timeframe || "-"}</div>
                {trade.reason && <div className={styles.muted}>{trade.reason}</div>}
                <div className={styles.rowActions} style={{ marginTop: "0.4rem" }}>
                  <button className={styles.ghostButton} type="button" onClick={() => handleMissedEdit(trade)}>Edit</button>
                  <button className={styles.ghostButton} type="button" onClick={() => handleMissedDelete(trade.id)}>Delete</button>
                </div>
              </div>
            ))}
            {journal.missed_trades.length === 0 && <div className={styles.muted}>No missed trades logged.</div>}
          </div>
        </section>

        <section className={styles.card} id="milestones">
          <div className={styles.cardHeader}>
            <div className={styles.cardTitle}>Milestones</div>
            <div className={styles.cardHint}>Track progress and habits.</div>
          </div>
          <div className={styles.formGrid}>
            <input
              className={styles.input}
              placeholder="Milestone title"
              value={milestoneDraft.title}
              onChange={(e) => setMilestoneDraft((prev) => ({ ...prev, title: e.target.value }))}
            />
            <input
              className={styles.input}
              type="date"
              value={milestoneDraft.date}
              onChange={(e) => setMilestoneDraft((prev) => ({ ...prev, date: e.target.value }))}
            />
            <select
              className={styles.select}
              value={milestoneDraft.status}
              onChange={(e) => setMilestoneDraft((prev) => ({ ...prev, status: e.target.value as "open" | "done" }))}
            >
              <option value="open">Open</option>
              <option value="done">Done</option>
            </select>
            <textarea
              className={styles.textarea}
              placeholder="Notes"
              value={milestoneDraft.notes ?? ""}
              onChange={(e) => setMilestoneDraft((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </div>
          <div className={styles.formActions}>
            <button className={styles.primaryButton} type="button" onClick={handleMilestoneSubmit}>
              {editingMilestoneId ? "Update Milestone" : "Add Milestone"}
            </button>
            {editingMilestoneId && (
              <button
                className={styles.ghostButton}
                type="button"
                onClick={() => {
                  setMilestoneDraft(emptyMilestone());
                  setEditingMilestoneId(null);
                }}
              >
                Cancel
              </button>
            )}
          </div>
          <div className={styles.listGrid} style={{ marginTop: "1rem" }}>
            {journal.milestones.map((milestone) => (
              <div key={milestone.id} className={styles.listCard}>
                <div className={styles.listTitle}>{milestone.title}</div>
                <div className={styles.muted}>{milestone.date} · {milestone.status}</div>
                {milestone.notes && <div className={styles.muted}>{milestone.notes}</div>}
                <div className={styles.rowActions} style={{ marginTop: "0.4rem" }}>
                  <button className={styles.ghostButton} type="button" onClick={() => handleMilestoneEdit(milestone)}>Edit</button>
                  <button className={styles.ghostButton} type="button" onClick={() => handleMilestoneDelete(milestone.id)}>Delete</button>
                </div>
              </div>
            ))}
            {journal.milestones.length === 0 && <div className={styles.muted}>No milestones yet.</div>}
          </div>
        </section>

        <div className={styles.footerNote}>
          Drafts are saved locally in your browser and synced to your account when logged in.
        </div>
      </div>
    </div>
  );
}
